import { Navigate } from "react-router-dom";
import { WorkspaceProvider, useWorkspace } from "@/hooks/useWorkspace";
import { useRole } from "@/hooks/useRole";
import WorkspaceLoadingSkeleton from "@/components/workspace/WorkspaceLoadingSkeleton";

/**
 * Role-aware landing decision for a tenant host's `/admin`.
 *
 * Owners + administrators land on the new Workspace Dashboard; every other role
 * (photographer / salesperson) keeps landing on Galleries, exactly as before.
 * The role comes from the existing `useRole` hook (never hardcoded), so it stays
 * in step with the workspace's authorisation.
 *
 * It needs a WorkspaceProvider to resolve the role, so this wraps itself in one.
 * That means a brief extra workspaces fetch before the redirect fires; the
 * destination then mounts WorkspaceLayout's own provider. We wait until the fetch
 * has settled so a not-yet-loaded role can't misroute an admin to Galleries.
 */
const LandingResolver = () => {
  const { isLoading, hasFetched } = useWorkspace();
  const { isAdmin } = useRole();

  if (isLoading || !hasFetched) {
    return <WorkspaceLoadingSkeleton />;
  }

  return <Navigate to={isAdmin ? "/admin/dashboard" : "/admin/galleries"} replace />;
};

const WorkspaceLanding = () => (
  <WorkspaceProvider>
    <LandingResolver />
  </WorkspaceProvider>
);

export default WorkspaceLanding;
